import React from "react";
import { EVENT_EVALUATION_TIMEFRAMES, INSIGHTS_EVENT_TYPES } from "../config/insightsEventTypes";
import "./ProfileNotifications.css";

const initialPreferences = () => ({ version: 1, type: null, favouritesOnly: false, rangeExtremum: "ANY", eventTypes: {} });

export default function ProfileNotifications({ notifications, submitCommand, revision }) {
  const [draft, setDraft] = React.useState(() => notifications || initialPreferences());
  const [status, setStatus] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setDraft(notifications || initialPreferences());
    setStatus("");
  }, [notifications]);

  const updateEvent = (eventType, timeframe, enabled) => {
    setDraft((current) => ({
      ...current,
      eventTypes: {
        ...current.eventTypes,
        [eventType]: { ...(current.eventTypes?.[eventType] || {}), [`_${timeframe}`]: enabled },
      },
    }));
  };

  const save = async () => {
    if (!revision || saving) return;
    setSaving(true);
    setStatus("Waiting for App backend to apply your changes…");
    try {
      const result = await submitCommand({
        type: "EVENT_NOTIFICATION_UPDATE",
        expectedSnapshotRevision: revision,
        payload: { eventNotifications: draft },
      });
      setStatus(result.acknowledgement?.accepted === true
        ? "Event alerts saved."
        : result.acknowledgement?.message || "Still waiting for App backend synchronization. Your selections have not been confirmed.");
    } catch (error) {
      setStatus(error?.message || "Event alerts could not be saved. Your selections are still here; please retry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="profile-card" aria-labelledby="profile-notifications-title">
      <h2 id="profile-notifications-title">Event alerts</h2>
      <p>Choose which market events CryptemberBot sends you on Telegram. Alerts are off until you enable them.</p>
      <div className="profile-notification-options">
        <label className="profile-field">Direction
          <select value={draft.type || "ALL"} onChange={(event) => setDraft((current) => ({
            ...current, type: event.target.value === "ALL" ? null : event.target.value,
          }))}>
            <option value="ALL">Bullish and bearish</option>
            <option value="Bullish">Bullish only</option>
            <option value="Bearish">Bearish only</option>
          </select>
        </label>
        <label className="profile-field">Source extremum
          <select value={draft.rangeExtremum || "ANY"} onChange={(event) => setDraft((current) => ({
            ...current, rangeExtremum: event.target.value,
          }))} aria-describedby="profile-range-extremum-help">
            <option value="ANY">Any</option>
            <option value="EXTREMES">Range extremes only</option>
            <option value="HIGH">Range high only</option>
            <option value="LOW">Range low only</option>
          </select>
        </label>
        <label className="profile-toggle">
          <input type="checkbox" checked={draft.favouritesOnly === true}
            onChange={(event) => setDraft((current) => ({ ...current, favouritesOnly: event.target.checked }))} />
          <span>Only favourites and markets I am trading</span>
        </label>
      </div>
      <p id="profile-range-extremum-help" className="mt-3">Range filters require the source wick to be the highest or lowest
        in the full retained candle history for that market and timeframe, before the trigger candle.
        Supported: Grab, Extremum Visit, Order Block Visit and RSI Divergence. Events without recorded range evidence
        are excluded when a range filter is selected.</p>
      <div className="profile-event-matrix-wrap">
        <table className="profile-event-matrix">
          <thead><tr><th scope="col">Event</th>{EVENT_EVALUATION_TIMEFRAMES.map((timeframe) => (
            <th scope="col" key={timeframe}>{timeframe}</th>
          ))}</tr></thead>
          <tbody>{INSIGHTS_EVENT_TYPES.map(({ value, label }) => (
            <tr key={value}><th scope="row">{label}</th>{EVENT_EVALUATION_TIMEFRAMES.map((timeframe) => (
              <td key={timeframe}><label>
                <input type="checkbox" aria-label={`${label} ${timeframe}`}
                  checked={draft.eventTypes?.[value]?.[`_${timeframe}`] === true}
                  onChange={(event) => updateEvent(value, timeframe, event.target.checked)} />
              </label></td>
            ))}</tr>
          ))}</tbody>
        </table>
      </div>
      <div className="profile-actions">
        <button type="button" disabled={!revision || saving} onClick={save}>{saving ? "Saving…" : "Save event alerts"}</button>
        <span role="status">{status || (!revision ? "Waiting for your profile from the App backend…" : "")}</span>
      </div>
    </section>
  );
}
